import { createFileRoute, useParams, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SlideGallery } from "../../../components/SlideGallery";
import { VideoPreview } from "../../../components/chat/VideoPreview";
import { ScriptPreview } from "../../../components/chat/ScriptPreview";
import { AudioPreview } from "../../../components/chat/AudioPreview";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../../components/ui/tabs";
import { ToggleSwitch } from "../../../components/ui/toggle-switch";
import { FileBrowser } from "../../../components/chat/FileBrowser";
import { FilePreview } from "../../../components/chat/FilePreview";
import { DirectorySelector } from "../../../components/chat/DirectorySelector";
import { useNavigate } from "@tanstack/react-router";
import slidePathMapping from "../../../services/slideMapping";
import { Settings } from '../../../components/settings/Settings'
import { ResourcesTab } from '../../../components/chat/ResourcesTab'
import { LayoutsTab } from '../../../components/gallery/LayoutsTab'
import { TemplatesTab } from '../../../components/gallery/TemplatesTab'
import { conversationAPI, slidesAPI } from '../../../services/api'


interface FileItem {
  name: string;
  path: string;
  type: "file" | "directory";
  size?: number;
  extension?: string;
}

// Cache for gallery data to prevent re-fetching when navigating back
const galleryDataCache = new Map<string, { data: any, timestamp: number }>()
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

export const Route = createFileRoute("/chat/$conversationId/gallery")({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      slide: search.slide as string | undefined,
      tab: search.tab as 'scenes' | 'layouts' | 'templates' | 'video' | 'script' | 'audio' | 'settings' | 'resources' | undefined,
    };
  },
  component: GalleryRoute,
  loader: async ({ params }) => {
    const cacheKey = `gallery_${params.conversationId}`
    const now = Date.now()

    // Check cache first
    const cached = galleryDataCache.get(cacheKey)
    if (cached && (now - cached.timestamp) < CACHE_TTL) {
      console.log('Gallery route - Using cached data')
      // Check for cross-redirect even with cached data
      if (cached.data.conversation?.collectionType === 'SLIDE') {
        throw redirect({
          to: '/chat/$conversationId/slide-workspace',
          params: { conversationId: params.conversationId },
          search: { tab: undefined }
        })
      }
      return cached.data
    }

    // Fetch fresh data
    console.log('Gallery route - Fetching fresh data')
    try {
      const [conversationResponse, slidesResponse] = await Promise.all([
        conversationAPI.getById(params.conversationId),
        slidesAPI.getByConversation(params.conversationId)
      ])

      // Redirect slide conversations to slide-workspace
      if (conversationResponse.conversation?.collectionType === 'SLIDE') {
        throw redirect({
          to: '/chat/$conversationId/slide-workspace',
          params: { conversationId: params.conversationId },
          search: { tab: undefined }
        })
      }

      const data = {
        conversation: conversationResponse.conversation,
        slides: slidesResponse.slides || []
      }

      // Cache the data
      galleryDataCache.set(cacheKey, { data, timestamp: now })

      return data
    } catch (error) {
      // Re-throw redirect errors
      if (error && typeof error === 'object' && 'isRedirect' in error) {
        throw error
      }
      console.error('Gallery route - Failed to load data:', error)
      return { conversation: null, slides: [] }
    }
  }
});

function GalleryRoute() {
  const { conversationId } = useParams({
    from: "/chat/$conversationId/gallery",
  });
  const searchParams = Route.useSearch();
  const loaderData = Route.useLoaderData();

  const navigate = useNavigate();

  // Check conversation types
  const isTemplateConversation = loaderData?.conversation?.collectionType === 'STYLE';
  const isSlideConversation = loaderData?.conversation?.collectionType === 'SLIDE';



  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  // Default to preview mode in both development and production
  const [viewMode, setViewMode] = useState<"code" | "preview">("preview");
  const [activeCodeTab, setActiveCodeTab] = useState("files");
  const [activePreviewTab, setActivePreviewTab] = useState<'scenes' | 'layouts' | 'templates' | 'video' | 'script' | 'audio' | 'settings' | 'resources'>(searchParams.tab || "scenes");

  useEffect(() => {
    // Always use tab from URL if available
    if (searchParams.tab) {
      setActivePreviewTab(searchParams.tab);
    }
  }, [searchParams.tab]);

  // Listen for workflow template selection event
  useEffect(() => {
    const handleOpenSettingsTab = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.conversationId === conversationId) {
        console.log('📋 Opening settings tab for template selection');
        setActivePreviewTab('settings');
        setViewMode('preview'); // Ensure we're in preview mode
      }
    };

    window.addEventListener('openSettingsTab', handleOpenSettingsTab);

    return () => {
      window.removeEventListener('openSettingsTab', handleOpenSettingsTab);
    };
  }, [conversationId]);

  const handleFileSelect = (file: FileItem) => {
    setSelectedFile(file);
  };

  const handleDirectoryChange = () => {
    // Reload file browser when directory changes
    setSelectedFile(null);
  };

  const [navigatingToSlide, setNavigatingToSlide] = useState<string | null>(null);
  
  // Pre-generate hashes for all slides on mount for instant navigation
  useEffect(() => {
    if (loaderData?.slides && loaderData.slides.length > 0) {
      const preGenerateHashes = async () => {
        const slidePaths = loaderData.slides.map((s: any) => s.path);
        console.log('Gallery - Pre-generating hashes for', slidePaths.length, 'slides');
        await slidePathMapping.populateMappings(slidePaths);
        console.log('Gallery - Hash pre-generation complete');
      };
      preGenerateHashes();
    }
  }, [loaderData?.slides]);
  
  const handleSlideClick = async (slidePath: string) => {
    // Set loading state immediately for instant feedback
    setNavigatingToSlide(slidePath);
    
    // Small delay to show loading state before navigation
    await new Promise(resolve => requestAnimationFrame(() => resolve(undefined)));
    
    // Generate hash for the slide path and navigate to slide route
    console.log("Gallery - Generating hash for slide path:", slidePath);
    const slideHash = await slidePathMapping.generateHashForPath(slidePath);
    console.log("Gallery - Generated hash:", slideHash);
    
    navigate({
      to: "/chat/$conversationId/slide/$slideHash",
      params: {
        conversationId,
        slideHash,
      },
      search: { 
        slide: undefined, 
        fromTab: activePreviewTab // Store current tab for back navigation
      },
    });
  };

  const handleVideoPreviewClick = () => {
    setActivePreviewTab("video");
  };

  // Handle viewMode changes with production restrictions
  const handleViewModeChange = (checked: boolean) => {
    // In production, only allow "preview" mode
    if (import.meta.env.PROD) {
      setViewMode("preview");
    } else {
      setViewMode(checked ? "preview" : "code");
    }
  };

  return (
    <>
      {/* Content Area */}
      <div className="flex-1 flex flex-col min-h-0">
        {viewMode === "code" ? (
          /* Code Editor Mode */
          <div className="h-full flex">
            {/* File Browser Sidebar */}
            <div className="w-64 min-w-64 max-w-64 bg-background/60 backdrop-blur-sm flex flex-col border-r border-border/30">
              <Tabs
                value={activeCodeTab}
                onValueChange={setActiveCodeTab}
                className="h-full flex flex-col"
              >
                <div className="flex-shrink-0 p-2">
                  <TabsList className="grid w-full grid-cols-2 h-7">
                    <TabsTrigger value="files" className="text-xs h-6">
                      Files
                    </TabsTrigger>
                    <TabsTrigger value="outline" className="text-xs h-6">
                      Outline
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent
                  value="files"
                  className="flex-1 overflow-hidden mt-0"
                >
                  <div className="h-full flex flex-col">
                    <div className="flex-shrink-0 px-2 pb-2">
                      <DirectorySelector
                        conversationId={conversationId}
                        onDirectoryChange={handleDirectoryChange}
                      />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <FileBrowser
                        onFileSelect={handleFileSelect}
                        selectedFile={selectedFile}
                        conversationId={conversationId}
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent
                  value="outline"
                  className="flex-1 overflow-hidden mt-0"
                >
                  <div className="p-3 text-xs text-muted-foreground">
                    Code outline coming soon...
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Code Editor Canvas */}
            <div className="flex-1 bg-background min-w-0 overflow-hidden">
              <FilePreview
                file={selectedFile}
                conversationId={conversationId}
                onFileSelect={handleFileSelect}
              />
            </div>
          </div>
        ) : (
          /* Preview & Slides Mode - Direct content without duplicate tabs */
          <div className="h-full bg-background overflow-hidden">
            <Tabs
              value={activePreviewTab}
              onValueChange={(value) => setActivePreviewTab(value as typeof activePreviewTab)}
              className="h-full flex flex-col"
            >
              {/* Tab Navigation */}
              <div className="flex-shrink-0 border-b bg-card px-4">
                <TabsList className="h-12 bg-transparent border-0 w-full justify-start">
                  <TabsTrigger value="scenes" className="data-[state=active]:bg-background">
                    Scenes
                  </TabsTrigger>
                  {isTemplateConversation && (
                    <TabsTrigger value="layouts" className="data-[state=active]:bg-background">
                      Layouts
                    </TabsTrigger>
                  )}
                  {isSlideConversation && (
                    <TabsTrigger value="templates" className="data-[state=active]:bg-background">
                      Templates
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="video" className="data-[state=active]:bg-background">
                    Video
                  </TabsTrigger>
                  <TabsTrigger value="script" className="data-[state=active]:bg-background">
                    Script
                  </TabsTrigger>
                  <TabsTrigger value="audio" className="data-[state=active]:bg-background">
                    Audio
                  </TabsTrigger>
                  <TabsTrigger value="settings" className="data-[state=active]:bg-background">
                    Settings
                  </TabsTrigger>
                  <TabsTrigger value="resources" className="data-[state=active]:bg-background">
                    Resources
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Tab Content - Full height */}
              <div className="flex-1 overflow-hidden">
                {/* Scenes Tab */}
                <TabsContent
                  value="scenes"
                  className="h-full mt-0 data-[state=active]:flex overflow-y-auto"
                >
                  <div className="h-full w-full">
                    <SlideGallery
                      conversationId={conversationId}
                      onSlideClick={handleSlideClick}
                      onVideoPreviewClick={handleVideoPreviewClick}
                      cachedSlides={loaderData?.slides}
                      cachedConversation={loaderData?.conversation}
                      navigatingToSlide={navigatingToSlide}
                    />
                  </div>
                </TabsContent>

                {/* Layouts Tab - Template workflow layouts */}
                {isTemplateConversation && (
                  <TabsContent
                    value="layouts"
                    className="h-full mt-0 data-[state=active]:flex overflow-y-auto"
                  >
                    <div className="h-full w-full">
                      <LayoutsTab conversationId={conversationId} />
                    </div>
                  </TabsContent>
                )}

                {/* Templates Tab - Public slide templates */}
                {isSlideConversation && (
                  <TabsContent
                    value="templates"
                    className="h-full mt-0 data-[state=active]:flex overflow-y-auto"
                  >
                    <div className="h-full w-full">
                      <TemplatesTab conversationId={conversationId} />
                    </div>
                  </TabsContent>
                )}

                {/* Video Tab - Using flex display for proper height */}
                <TabsContent
                  value="video"
                  className="h-full mt-0 flex flex-col"
                  forceMount
                  style={{ display: activePreviewTab === "video" ? "flex" : "none" }}
                >
                  <VideoPreview
                    conversationId={conversationId}
                    onBackToGallery={() => setActivePreviewTab("scenes")}
                    className="flex-1"
                  />
                </TabsContent>

                {/* Script Tab */}
                <TabsContent
                  value="script"
                  className="h-full mt-0 data-[state=active]:flex overflow-y-auto"
                >
                  <div className="h-full w-full">
                    <ScriptPreview conversationId={conversationId} />
                  </div>
                </TabsContent>

                {/* Audio Tab */}
                <TabsContent
                  value="audio"
                  className="h-full mt-0 data-[state=active]:flex overflow-y-auto"
                >
                  <div className="h-full w-full">
                    <AudioPreview conversationId={conversationId} />
                  </div>
                </TabsContent>

                {/* Settings Tab */}
                <TabsContent
                  value="settings"
                  className="h-full mt-0 data-[state=active]:flex overflow-y-auto"
                >
                  <div className="h-full w-full">
                    <Settings />
                  </div>
                </TabsContent>

                {/* Resources Tab */}
                <TabsContent
                  value="resources"
                  className="h-full mt-0 data-[state=active]:flex overflow-y-auto"
                >
                  <div className="h-full w-full">
                    <ResourcesTab conversationId={conversationId} />
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        )}
      </div>

      {/* Floating Toggle in Bottom Right - Hidden in production */}
      {!import.meta.env.PROD && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="bg-background/95 backdrop-blur-md border border-border/50 rounded-full shadow-xl p-2">
            <ToggleSwitch
              checked={viewMode === "preview"}
              onCheckedChange={handleViewModeChange}
              leftLabel="Code"
              rightLabel="Scenes"
              leftIcon={
                <svg
                  className="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                  />
                </svg>
              }
              rightIcon={
                <svg
                  className="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              }
              className="h-8"
            />
          </div>
        </div>
      )}
    </>
  );
}
